import { motion } from "framer-motion";
import { useState } from "react";
import { Check, Sparkles, ShieldCheck, Loader2 } from "lucide-react";
import SubscribeDialog from "@/components/site/SubscribeDialog";
import RemovalCheckoutDialog from "@/components/site/RemovalCheckoutDialog";

const tiers = [
  {
    name: "Free Audit",
    price: "$0",
    priceUnit: "no obligation",
    tagline: "Start here.",
    features: [
      "Full review-by-review policy analysis",
      "List of removal-eligible reviews",
      "Estimated timeline & recovery plan",
      "No credit card required",
    ],
    cta: "Get free audit",
    href: "#audit",
    accent: "gold",
    ribbon: null,
  },
  {
    name: "Per-Review Removal",
    price: "$195",
    priceUnit: "per removed review",
    tagline: "You only pay after it's gone.",
    features: [
      "Full filing through Google's channels",
      "Escalation on stalled cases",
      "Detailed proof-of-removal report",
      "No removal → no charge, guaranteed",
      "Invoice on your dashboard after removal — card or ACH",
    ],
    cta: "Report a review — $195",
    href: "#",
    accent: "emerald",
    ribbon: "Results Guaranteed",
    highlight: true,
    action: "removal",
  },
  {
    name: "Monitoring",
    price: "$45",
    priceUnit: "per month",
    tagline: "Keep the wins.",
    features: [
      "24/7 review monitoring",
      "Instant alerts on new negatives",
      "Monthly reputation report",
      "Priority queue for future removals",
      "Cancel anytime",
    ],
    cta: "Start monitoring",
    href: "#audit",
    accent: "gold",
    ribbon: null,
  },
];

/**
 * Animated tracing beam border — a rotating conic-gradient behind
 * the card creates a glowing edge that travels around the perimeter.
 */
function TracingBeamCard({ children }) {
  return (
    <div className="relative rounded-3xl p-[1.5px]">
      <div className="absolute inset-0 rounded-3xl overflow-hidden">
        <motion.div
          aria-hidden
          animate={{ rotate: 360 }}
          transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
          className="absolute -inset-1/2"
          style={{
            background:
              "conic-gradient(from 0deg, transparent 0deg, transparent 260deg, #10B981 320deg, #D4AF37 340deg, transparent 360deg)",
          }}
        />
      </div>
      <div className="relative rounded-3xl bg-[#0A0F1C]">{children}</div>
    </div>
  );
}

function Tier({ t, i, onOpenSubscribe, onOpenRemoval }) {
  const isHighlight = t.highlight;
  const [loading, setLoading] = useState(false);

  const onCta = async (e) => {
    if (t.action === "removal") {
      e.preventDefault();
      onOpenRemoval();
      return;
    }
    if (t.name !== "Monitoring") return; // Free Audit keeps anchor behavior
    e.preventDefault();
    onOpenSubscribe({
      packageId: "monitoring_month",
      label: "Monitoring & Maintenance",
      priceLabel: "$45/mo",
      priceUsd: 45,
    });
  };

  const card = (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.55, delay: i * 0.1 }}
      className={`relative rounded-3xl p-8 flex flex-col h-full ${
        isHighlight
          ? "bg-gradient-to-b from-[#10B981]/[0.10] to-[#10B981]/[0.02]"
          : "border border-white/10 bg-white/[0.02]"
      }`}
    >
      {t.ribbon && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-[#D4AF37] text-[#0A0F1C] text-[11px] font-bold uppercase tracking-widest px-3 py-1 shadow-lg shadow-[#D4AF37]/30">
            <Sparkles className="w-3 h-3" />
            {t.ribbon}
          </div>
        </div>
      )}

      <div>
        <div className="text-xs uppercase tracking-[0.16em] text-white/40 font-semibold">
          {t.name}
        </div>
        <div className="mt-3 flex items-baseline gap-2">
          <span className="font-display text-5xl font-bold text-white tabular-nums">{t.price}</span>
          <span className="text-white/45 text-sm">{t.priceUnit}</span>
        </div>
        <div className="mt-2 text-sm text-white/60">{t.tagline}</div>
      </div>

      <ul className="mt-8 space-y-3 flex-1">
        {t.features.map((f, idx) => (
          <motion.li
            key={f}
            initial={{ opacity: 0, x: -6 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.3, delay: 0.2 + idx * 0.05 }}
            className="flex items-start gap-3 text-sm text-white/70"
          >
            <span
              className={`shrink-0 mt-0.5 w-5 h-5 rounded-full grid place-items-center ${
                isHighlight
                  ? "bg-[#10B981] text-white"
                  : "bg-white/[0.05] text-[#10B981] border border-[#10B981]/30"
              }`}
            >
              <Check className="w-3 h-3" strokeWidth={3} />
            </span>
            <span>{f}</span>
          </motion.li>
        ))}
      </ul>

      <a
        href={t.href}
        onClick={onCta}
        data-testid={`pricing-cta-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
        className={`group mt-8 relative inline-flex items-center justify-center gap-2 h-11 rounded-full font-semibold text-sm transition-colors overflow-hidden ${
          isHighlight
            ? "bg-[#10B981] hover:bg-[#0ea774] text-white shadow-lg shadow-emerald-500/30"
            : "border border-white/15 hover:border-white/30 text-white bg-white/[0.02] hover:bg-white/[0.05]"
        } ${loading ? "opacity-70 pointer-events-none" : ""}`}
      >
        {isHighlight && (
          <span
            aria-hidden
            className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/20 to-transparent"
          />
        )}
        <span className="relative inline-flex items-center gap-2">
          {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          {loading ? "Starting…" : t.cta}
        </span>
      </a>

      {isHighlight && (
        <div className="mt-4 flex items-center justify-center gap-2 text-[11px] text-white/50">
          <ShieldCheck className="w-3.5 h-3.5 text-[#10B981]" />
          No upfront charge — invoice sent only after removal.
        </div>
      )}
    </motion.div>
  );

  if (isHighlight) {
    return (
      <div className="lg:-translate-y-4">
        <TracingBeamCard>{card}</TracingBeamCard>
      </div>
    );
  }
  return card;
}

export default function Pricing() {
  const [subscribe, setSubscribe] = useState(null);
  const [removal, setRemoval] = useState(false);

  return (
    <section id="pricing" className="relative py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-[0.2em] text-[#10B981] font-semibold">
            Pricing
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mt-3 leading-tight">
            Pay for results. <span className="text-white/50">Not promises.</span>
          </h2>
          <p className="mt-4 text-white/55 max-w-lg text-base">
            No retainers. No monthly minimums for removals. $195 is invoiced only after a review is
            successfully removed — verifiable in your Google Business dashboard.
          </p>
        </div>

        <div className="mt-16 grid md:grid-cols-3 gap-6 items-stretch">
          {tiers.map((t, i) => (
            <Tier
              key={t.name}
              t={t}
              i={i}
              onOpenSubscribe={setSubscribe}
              onOpenRemoval={() => setRemoval(true)}
            />
          ))}
        </div>

        <p className="mt-10 text-center text-xs text-white/40">
          Card and ACH bank transfer accepted. ACH payments typically settle within ~4 business days.
        </p>
      </div>

      <SubscribeDialog
        open={!!subscribe}
        onClose={() => setSubscribe(null)}
        packageId={subscribe?.packageId}
        label={subscribe?.label}
        priceLabel={subscribe?.priceLabel}
        priceUsd={subscribe?.priceUsd}
      />
      <RemovalCheckoutDialog
        open={removal}
        onClose={() => setRemoval(false)}
      />
    </section>
  );
}
